import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api.js';
import {
  TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY,
  IDLE_TIMEOUT_MINUTES,
  ACTIVITY_STORAGE_KEY,
  SESSION_PING_KEY,
  LOGOUT_REASON_KEY,
  BOUNCED_USER_KEY,
} from '../config/index.js';

const AuthContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

const IDLE_MS = IDLE_TIMEOUT_MINUTES * 60_000;

/**
 * Tell the other tabs in this browser that an account just signed in (QA 7843).
 * Carries the user id ONLY — never the token. A 'storage' event is used rather
 * than BroadcastChannel because it reaches tabs whose session lives in
 * sessionStorage too, and it fires only in OTHER tabs, never the one writing.
 */
function announceSignIn(userId) {
  try {
    localStorage.setItem(SESSION_PING_KEY, JSON.stringify({ userId, at: Date.now() }));
  } catch { /* storage unavailable (private mode) — cross-tab sync degrades, auth still works */ }
}

/** Stamp last interaction. In localStorage deliberately: every tab shares ONE
 * idle clock, so working in one tab keeps the others alive rather than a
 * background tab signing out an active user (they share the same token). */
function touchActivity() {
  try {
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

// "Keep me signed in" persists to localStorage; otherwise the session lives in
// sessionStorage and clears when the tab closes.
function persistSession(user, token, remember) {
  const store = remember ? localStorage : sessionStorage;
  const other = remember ? sessionStorage : localStorage;
  other.removeItem(TOKEN_STORAGE_KEY);
  other.removeItem(USER_STORAGE_KEY);
  store.setItem(TOKEN_STORAGE_KEY, token);
  store.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  touchActivity();
  announceSignIn(user?.id);
}

function clearSession() {
  // Remember WHO is being signed out before the record goes. A route guard may
  // be about to bounce this tab to /login stamping `state.from` with the page
  // still on screen; that state survives the sign-out, so the login screen
  // needs to know whose page it was to avoid replaying it to the next user
  // (QA 7878). Stamped here rather than in each caller so every exit path —
  // menu logout, idle timeout, expiry, signed-in-elsewhere — is covered.
  // ALWAYS stamp something: an absent key is meaningful on the other side — it
  // means no session ended in this tab, i.e. a cold deep link, which is still
  // replayed. If the record is unreadable we must still record that SOMEONE
  // left, or their page would be replayed to whoever signs in next.
  let departing = 'unknown';
  try {
    const stored = readStored(USER_STORAGE_KEY);
    const id = stored ? JSON.parse(stored)?.id : null;
    if (id != null) departing = String(id);
  } catch { /* unreadable record — the sentinel matches nobody, so nothing replays */ }
  try {
    sessionStorage.setItem(BOUNCED_USER_KEY, departing);
  } catch { /* storage unavailable (private mode) */ }

  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(USER_STORAGE_KEY);
}

function readStored(key) {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, restore the cached user and re-validate the token against the API.
  useEffect(() => {
    const token = readStored(TOKEN_STORAGE_KEY);
    const cached = readStored(USER_STORAGE_KEY);

    if (!token) {
      setLoading(false);
      return;
    }

    if (cached) {
      try {
        setUser(JSON.parse(cached));
      } catch {
        clearSession();
      }
    }

    api
      .me()
      .then((fresh) => {
        setUser(fresh);
        const remember = localStorage.getItem(TOKEN_STORAGE_KEY) != null;
        (remember ? localStorage : sessionStorage).setItem(
          USER_STORAGE_KEY,
          JSON.stringify(fresh)
        );
      })
      .catch((err) => {
        // Only clear on an actual auth rejection (api.js sets err.status from
        // the response). A network error (offline PWA reload) has no status —
        // keep the cached session; a later real 401 is handled by the API layer.
        if (err?.status === 401 || err?.status === 403) {
          clearSession();
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Latest user without re-subscribing the listeners below on every change.
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);

  /**
   * Idle sign-out (QA 7842). The session used to stay usable indefinitely — no
   * inactivity limit anywhere, and the JWT is valid for 7 days.
   *
   * The clock is shared through localStorage rather than kept per-tab, so the
   * timer reflects the person, not the tab: without that, a forgotten
   * background tab would sign out a user actively working in another one.
   * Polled rather than a single long timer so a laptop that was asleep is
   * judged on elapsed wall-clock, not on a timer that never fired.
   */
  useEffect(() => {
    if (!user) return undefined;

    // Throttle writes — these events fire continuously under the mouse.
    let lastTouch = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastTouch < 15_000) return;
      lastTouch = now;
      touchActivity();
    };
    const EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'focus'];
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    touchActivity(); // start the clock at sign-in/mount

    const timer = setInterval(() => {
      const last = Number(localStorage.getItem(ACTIVITY_STORAGE_KEY)) || 0;
      // No stamp yet (storage blocked) — don't sign anyone out on a guess.
      if (!last) return;
      if (Date.now() - last < IDLE_MS) return;
      try { sessionStorage.setItem(LOGOUT_REASON_KEY, 'idle'); } catch { /* ignore */ }
      clearSession();
      setUser(null); // route guards send the user to /login
    }, 30_000);

    return () => {
      clearInterval(timer);
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [user]);

  /**
   * One account per browser (QA 7843). Two accounts could be signed in at once
   * across tabs: with sessionStorage each tab held its own session outright,
   * and with localStorage the second sign-in silently overwrote the shared
   * token while the first tab kept rendering the previous user — that tab then
   * issued API calls as someone else, which is the dangerous half.
   *
   * On a different identity signing in, reload rather than clearing storage:
   * clearing would destroy the token that now belongs to the NEW session.
   * Reloading re-reads storage, so the shared-localStorage tab adopts the new
   * user (UI and token agree again), while a sessionStorage tab finds its own
   * token gone and lands on /login. Both converge on a single identity.
   */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== SESSION_PING_KEY || !e.newValue) return;
      const current = userRef.current;
      if (!current) return; // not signed in here — nothing to displace
      let payload;
      try { payload = JSON.parse(e.newValue); } catch { return; }
      if (!payload?.userId || payload.userId === current.id) return; // same person
      try {
        sessionStorage.setItem(LOGOUT_REASON_KEY, 'signed-in-elsewhere');
        // Only this tab's own (per-tab) copy — never the shared localStorage
        // token, which the new session depends on.
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(USER_STORAGE_KEY);
      } catch { /* ignore */ }
      window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  /**
   * Step 1: submit credentials. Returns:
   *   { mfaRequired: true, challengeId, expiresInSeconds, devOtp? } — caller must call verifyMfa, OR
   *   { mfaRequired: false, user } — session established.
   */
  const login = useCallback(async (identity, password, remember = false, turnstileToken) => {
    const res = await api.login({ identity, password, remember, turnstileToken });
    if (res.mfaRequired) {
      // Hand the challenge back whole. Picking fields here dropped the code's
      // TTL, so the first countdown sat at 00:00 while a resend — which returns
      // the raw response — counted down normally (QA 8707).
      return { ...res, mfaRequired: true };
    }
    persistSession(res.user, res.token, remember);
    setUser(res.user);
    return { mfaRequired: false, user: res.user };
  }, []);

  /**
   * SuperTokens-verified sign-in: credentials checked by the self-hosted ST core,
   * then the app session is established from the returned { user, token }. Mirrors
   * login()'s return shape (no MFA step on this path).
   */
  const loginViaSupertokens = useCallback(async (identity, password, remember = false) => {
    const res = await api.loginViaSupertokens({ identity, password });
    persistSession(res.user, res.token, remember);
    setUser(res.user);
    return { mfaRequired: false, user: res.user };
  }, []);

  /** Step 2: complete an MFA challenge and establish the session. */
  const verifyMfa = useCallback(async (challengeId, code, remember = false) => {
    const { user: u, token } = await api.verifyMfa({ challengeId, code });
    persistSession(u, token, remember);
    setUser(u);
    return u;
  }, []);

  const resendMfa = useCallback(async (challengeId) => {
    return api.resendMfa({ challengeId });
  }, []);

  /** Establish a session from an already-issued { user, token } (e.g. connector
   * self-registration completes with a session in the verify response). */
  const applySession = useCallback((u, token, remember = false) => {
    persistSession(u, token, remember);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, name, password) => {
    const { user: u, token } = await api.register({ email, name, password });
    persistSession(u, token, true);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    loginViaSupertokens,
    verifyMfa,
    resendMfa,
    applySession,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
